/**
 * ClientPersonaLibrary
 *
 * Faceted + semantic library browser for the ClientPersona collection.
 * Replaces the dropdown-driven persona pickers used in the simulation wizards
 * and provides curators with search, filter, clone, and lineage tooling.
 *
 * Phase 2 deliverable. Backend endpoints live under /api/admin/personas/client/*.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Grid, Paper, Typography, TextField, Button, IconButton, Drawer,
  Chip, MenuItem, Select, FormControl, InputLabel, FormControlLabel,
  Checkbox, Switch, Slider, Accordion, AccordionSummary, AccordionDetails,
  Card, CardContent, CardActions, Avatar, Tabs, Tab, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert, CircularProgress, Tooltip, Divider,
  InputAdornment, Stack, Autocomplete
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PsychologyIcon from '@mui/icons-material/Psychology';
import PersonaTrainingSharing, { trainingSharingLabel } from './PersonaTrainingSharing';
import {
  searchPersonas,
  clonePersona,
  getPersonaUsage,
  getPersonaVariants,
  getSimilarPersonas,
  regeneratePersonaEmbedding,
  listServiceGroupsForPicker
} from '../../../services/personaLibraryApi';

const PLATFORM_ADMIN_ROLES = ['admin', 'platform_admin', 'super_admin'];
const isPlatformAdminRole = (userRole) =>
  (Array.isArray(userRole) ? userRole : [userRole]).some(r => PLATFORM_ADMIN_ROLES.includes(r));

// ──────────────────────────────────────────────────────────────────────────────
// Filter option vocabulary (mirrors the existing ClientPersonaEditor)
// ──────────────────────────────────────────────────────────────────────────────

const SEVERITY_OPTIONS = ['mild', 'moderate', 'severe'];
const GENDER_OPTIONS = ['female', 'male', 'non-binary', 'transgender_female', 'transgender_male', 'prefer_not_to_say'];
const AGE_RANGE_OPTIONS = ['under_18', '18-24', '25-34', '35-44', '45-54', '55-64', '65+'];
const COMM_STYLE_OPTIONS = ['terse', 'concrete', 'narrative', 'analytical', 'scattered', 'tangential'];
const EDUCATION_OPTIONS = ['none', 'secondary', 'trades', 'undergraduate', 'postgraduate'];
const STATUS_OPTIONS = ['draft', 'published', 'archived'];

const CATEGORY_LABELS = {
  mood_disorders: 'Mood',
  anxiety_disorders: 'Anxiety',
  psychotic_disorders: 'Psychotic',
  personality_disorders: 'Personality',
  substance_use: 'Substance Use',
  neurodevelopmental_disorders: 'Neurodevelopmental',
  trauma_and_stress: 'Trauma / Stress',
  eating_disorders: 'Eating',
  liaison_psychiatry: 'Liaison',
  child_and_adolescent: 'Child / Adolescent',
  older_adult: 'Older Adult',
  forensic: 'Forensic',
  specialist: 'Specialist',
  cross_cutting_skills: 'Cross-cutting'
};

const ORG_SCOPE_OPTIONS = [
  { value: 'all', label: 'All accessible' },
  { value: 'own', label: 'My org only' },
  { value: 'platform', label: 'Platform library' }
];

// ──────────────────────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────────────────────

/**
 * @param {object} props
 * @param {string} props.token
 * @param {'page'|'picker'} [props.mode='page']    'picker' shows Select actions
 *                                                  on cards and in the drawer.
 * @param {function} [props.onSelectPersona]       Called with the selected
 *                                                  persona object when in
 *                                                  picker mode.
 * @param {string} [props.preselectedKey]          Highlight a starting persona
 *                                                  in the result grid.
 */
const ClientPersonaLibrary = ({ token, mode = 'page', onSelectPersona, preselectedKey, userRole, apiScope }) => {
  // Lecturers (apiScope 'training') pick and view only; cloning stays in the admin library.
  const canClone = apiScope !== 'training';
  const [filters, setFilters] = useState({
    q: '',
    semantic: true,
    conditions: [],
    severity: [],
    ageRange: [],
    gender: [],
    languages: [],
    communicationStyle: [],
    educationLevel: [],
    tags: [],
    category: [],
    status: ['published', 'draft'],
    resistanceEnabled: null,
    hasOnboardingProfile: false,
    hasImage: false,
    culturalContextQuery: '',
    numericRanges: {
      vocabularyLevel: [1, 10],
      emotionalArticulation: [1, 10],
      introspectiveCapacity: [1, 10]
    }
  });

  const [orgScope, setOrgScope] = useState('all');
  const [sortBy, setSortBy] = useState('recently_updated');
  const [pagination, setPagination] = useState({ limit: 30, offset: 0 });

  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [facetCounts, setFacetCounts] = useState({});
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedPersona, setSelectedPersona] = useState(null);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneSource, setCloneSource] = useState(null);

  // ── Debounced search ──────────────────────────────────────────────────────
  useEffect(() => {
    const handle = setTimeout(() => runSearch(), 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, orgScope, sortBy, pagination.offset]);

  const runSearch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await searchPersonas({
        filters,
        pagination: { ...pagination, sort: sortBy },
        orgScope
      }, token, apiScope);
      setResults(data.results || []);
      setTotal(data.total || 0);
      setFacetCounts(data.facetCounts || {});
      setMeta(data.meta || {});
    } catch (e) {
      setError(e.msg || e.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination, sortBy, orgScope, token, apiScope]);

  const updateFilter = (key, value) => {
    setFilters(f => ({ ...f, [key]: value }));
    setPagination(p => ({ ...p, offset: 0 }));
  };

  const toggleMultiFilter = (key, value) => {
    setFilters(f => {
      const current = f[key] || [];
      const next = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...f, [key]: next };
    });
    setPagination(p => ({ ...p, offset: 0 }));
  };

  const clearFilters = () => {
    setFilters(f => ({
      ...f,
      q: '',
      conditions: [], severity: [], ageRange: [], gender: [],
      languages: [], communicationStyle: [], educationLevel: [],
      tags: [], category: [],
      resistanceEnabled: null, hasOnboardingProfile: false, hasImage: false,
      culturalContextQuery: ''
    }));
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 200px)' }}>
      {/* Filter sidebar */}
      <Box sx={{ width: 300, overflowY: 'auto', pr: 1 }}>
        <PersonaFilterSidebar
          filters={filters}
          facetCounts={facetCounts}
          orgScope={orgScope}
          onOrgScope={setOrgScope}
          onToggle={toggleMultiFilter}
          onUpdate={updateFilter}
          onClear={clearFilters}
        />
      </Box>

      {/* Main content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Search bar */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              fullWidth
              placeholder='Search personas (e.g., "young trans woman with relational trauma")'
              value={filters.q}
              onChange={e => updateFilter('q', e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={filters.semantic}
                  onChange={e => updateFilter('semantic', e.target.checked)}
                />
              }
              label="Semantic"
              labelPlacement="end"
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Sort</InputLabel>
              <Select value={sortBy} onChange={e => setSortBy(e.target.value)} label="Sort">
                <MenuItem value="recently_updated">Recently updated</MenuItem>
                <MenuItem value="alphabetical">Alphabetical</MenuItem>
                <MenuItem value="similarity" disabled={!filters.q}>Similarity to query</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {loading ? 'Searching…' : `${total} persona${total === 1 ? '' : 's'}`}
              {meta.semanticApplied && (
                <Chip size="small" label="semantic re-ranked" color="primary" variant="outlined" sx={{ ml: 1 }} />
              )}
            </Typography>
            <Button size="small" startIcon={<RefreshIcon />} onClick={runSearch}>Refresh</Button>
          </Box>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Result grid */}
        <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
          {loading && results.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
          ) : results.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No personas match these filters.</Typography>
              <Button onClick={clearFilters} sx={{ mt: 1 }}>Clear filters</Button>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {results.map(persona => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={persona._id || persona.key}>
                  <PersonaCard
                    persona={persona}
                    mode={mode}
                    highlighted={preselectedKey && persona.key === preselectedKey}
                    onView={() => setSelectedPersona(persona)}
                    onClone={canClone ? () => { setCloneSource(persona); setCloneDialogOpen(true); } : undefined}
                    onSelect={onSelectPersona ? () => onSelectPersona(persona) : undefined}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Box>

      {/* Detail drawer */}
      <PersonaDetailDrawer
        persona={selectedPersona}
        token={token}
        mode={mode}
        apiScope={apiScope}
        onSharingChanged={(key, trainingSharing) => {
          setResults(rs => rs.map(r => (r.key === key ? { ...r, trainingSharing } : r)));
          setSelectedPersona(p => (p && p.key === key ? { ...p, trainingSharing } : p));
        }}
        onClose={() => setSelectedPersona(null)}
        onClone={canClone ? p => { setCloneSource(p); setCloneDialogOpen(true); } : undefined}
        onSelect={onSelectPersona ? (p) => { onSelectPersona(p); setSelectedPersona(null); } : undefined}
      />

      {/* Clone dialog */}
      <PersonaCloneDialog
        open={cloneDialogOpen}
        source={cloneSource}
        token={token}
        userRole={userRole}
        onClose={() => setCloneDialogOpen(false)}
        onCloned={() => { setCloneDialogOpen(false); runSearch(); }}
      />
    </Box>
  );
};

// ──────────────────────────────────────────────────────────────────────────────
// Filter sidebar
// ──────────────────────────────────────────────────────────────────────────────

const FacetGroup = ({ title, defaultExpanded, children }) => (
  <Accordion defaultExpanded={defaultExpanded} disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
      <Typography variant="subtitle2">{title}</Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ pt: 0 }}>{children}</AccordionDetails>
  </Accordion>
);

const FacetCheckList = ({ options, selected, counts, onToggle }) => (
  <Stack>
    {options.map(opt => {
      const value = typeof opt === 'string' ? opt : opt.value;
      const label = typeof opt === 'string' ? opt : opt.label;
      const count = counts?.find(c => c._id === value)?.count;
      const checked = selected.includes(value);
      return (
        <FormControlLabel
          key={value}
          sx={{ ml: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
          control={<Checkbox size="small" checked={checked} onChange={() => onToggle(value)} />}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <span>{label}</span>
              {count != null && <Typography variant="caption" color="text.secondary">({count})</Typography>}
            </Box>
          }
        />
      );
    })}
  </Stack>
);

const PersonaFilterSidebar = ({ filters, facetCounts, orgScope, onOrgScope, onToggle, onUpdate, onClear }) => {
  // Build dynamic condition options from the facet counts
  const conditionOptions = useMemo(() => {
    const fromFacets = (facetCounts.conditions || []).filter(c => c._id).map(c => c._id);
    return Array.from(new Set([...fromFacets, ...filters.conditions])).sort();
  }, [facetCounts.conditions, filters.conditions]);

  const tagOptions = useMemo(() => {
    const fromFacets = (facetCounts.tags || []).filter(c => c._id).map(c => c._id);
    return Array.from(new Set([...fromFacets, ...filters.tags])).sort();
  }, [facetCounts.tags, filters.tags]);

  const categoryOptions = Object.keys(CATEGORY_LABELS).map(value => ({ value, label: CATEGORY_LABELS[value] }));

  return (
    <Paper sx={{ p: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, px: 1 }}>
        <Typography variant="subtitle1">Filters</Typography>
        <Button size="small" onClick={onClear}>Clear</Button>
      </Box>

      <FacetGroup title="Org scope" defaultExpanded>
        <FormControl size="small" fullWidth>
          <Select value={orgScope} onChange={e => onOrgScope(e.target.value)}>
            {ORG_SCOPE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
      </FacetGroup>

      <FacetGroup title="Clinical" defaultExpanded>
        <Typography variant="caption" color="text.secondary">Conditions</Typography>
        <FacetCheckList
          options={conditionOptions}
          selected={filters.conditions}
          counts={facetCounts.conditions}
          onToggle={v => onToggle('conditions', v)}
        />
        <Divider sx={{ my: 1 }} />
        <Typography variant="caption" color="text.secondary">Severity</Typography>
        <FacetCheckList
          options={SEVERITY_OPTIONS}
          selected={filters.severity}
          counts={facetCounts.severity}
          onToggle={v => onToggle('severity', v)}
        />
        <Divider sx={{ my: 1 }} />
        <Typography variant="caption" color="text.secondary">Category</Typography>
        <FacetCheckList
          options={categoryOptions}
          selected={filters.category}
          counts={facetCounts.category}
          onToggle={v => onToggle('category', v)}
        />
      </FacetGroup>

      <FacetGroup title="Demographics">
        <Typography variant="caption" color="text.secondary">Age range</Typography>
        <FacetCheckList
          options={AGE_RANGE_OPTIONS}
          selected={filters.ageRange}
          counts={facetCounts.ageRange}
          onToggle={v => onToggle('ageRange', v)}
        />
        <Divider sx={{ my: 1 }} />
        <Typography variant="caption" color="text.secondary">Gender</Typography>
        <FacetCheckList
          options={GENDER_OPTIONS}
          selected={filters.gender}
          counts={facetCounts.gender}
          onToggle={v => onToggle('gender', v)}
        />
        <Divider sx={{ my: 1 }} />
        <TextField
          size="small"
          fullWidth
          label="Cultural context"
          placeholder="e.g. South Asian, Māori"
          value={filters.culturalContextQuery || ''}
          onChange={e => onUpdate('culturalContextQuery', e.target.value)}
          helperText="Matches culturalContextSummary"
        />
      </FacetGroup>

      <FacetGroup title="Behaviour">
        <Typography variant="caption" color="text.secondary">Communication style</Typography>
        <FacetCheckList
          options={COMM_STYLE_OPTIONS}
          selected={filters.communicationStyle}
          counts={facetCounts.communicationStyle}
          onToggle={v => onToggle('communicationStyle', v)}
        />
        <Divider sx={{ my: 1 }} />
        <Typography variant="caption" color="text.secondary">Education</Typography>
        <FacetCheckList
          options={EDUCATION_OPTIONS}
          selected={filters.educationLevel}
          counts={facetCounts.educationLevel}
          onToggle={v => onToggle('educationLevel', v)}
        />
        <Divider sx={{ my: 1 }} />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={filters.resistanceEnabled === true}
              onChange={e => onUpdate('resistanceEnabled', e.target.checked ? true : null)}
            />
          }
          label="Resistance enabled"
        />
        <NumericRangeSlider
          label="Vocabulary"
          value={filters.numericRanges.vocabularyLevel}
          onChange={v => onUpdate('numericRanges', { ...filters.numericRanges, vocabularyLevel: v })}
        />
        <NumericRangeSlider
          label="Emotional articulation"
          value={filters.numericRanges.emotionalArticulation}
          onChange={v => onUpdate('numericRanges', { ...filters.numericRanges, emotionalArticulation: v })}
        />
        <NumericRangeSlider
          label="Introspective capacity"
          value={filters.numericRanges.introspectiveCapacity}
          onChange={v => onUpdate('numericRanges', { ...filters.numericRanges, introspectiveCapacity: v })}
        />
      </FacetGroup>

      <FacetGroup title="Capabilities">
        <FormControlLabel
          control={<Checkbox size="small" checked={!!filters.hasOnboardingProfile} onChange={e => onUpdate('hasOnboardingProfile', e.target.checked)} />}
          label="Has onboarding profile"
        />
        <FormControlLabel
          control={<Checkbox size="small" checked={!!filters.hasImage} onChange={e => onUpdate('hasImage', e.target.checked)} />}
          label="Has image"
        />
      </FacetGroup>

      <FacetGroup title="Tags">
        <FacetCheckList
          options={tagOptions}
          selected={filters.tags}
          counts={facetCounts.tags}
          onToggle={v => onToggle('tags', v)}
        />
      </FacetGroup>

      <FacetGroup title="Status">
        <FacetCheckList
          options={STATUS_OPTIONS}
          selected={filters.status}
          counts={facetCounts.status}
          onToggle={v => onToggle('status', v)}
        />
      </FacetGroup>
    </Paper>
  );
};

const NumericRangeSlider = ({ label, value, onChange }) => (
  <Box sx={{ mt: 1 }}>
    <Typography variant="caption" color="text.secondary">{label}: {value[0]}–{value[1]}</Typography>
    <Slider
      value={value}
      onChange={(_, v) => onChange(v)}
      valueLabelDisplay="auto"
      min={1}
      max={10}
      step={1}
      size="small"
    />
  </Box>
);

// ──────────────────────────────────────────────────────────────────────────────
// Card
// ──────────────────────────────────────────────────────────────────────────────

const PersonaCard = ({ persona, mode = 'page', highlighted = false, onView, onClone, onSelect }) => {
  const conditions = persona.metadata?.conditions || [];
  const demo = persona.metadata?.demographics || {};
  const isClone = !!persona.lineage?.clonedFrom;
  const similarity = persona._similarityScore;
  const isPicker = mode === 'picker';

  return (
    <Card
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        ...(highlighted && { borderColor: 'primary.main', borderWidth: 2, boxShadow: 2 })
      }}
    >
      <Box sx={{ display: 'flex', p: 1.5, gap: 1.5 }}>
        <Avatar
          src={persona.personaImage?.url || undefined}
          sx={{ width: 56, height: 56, bgcolor: 'primary.light' }}
        >
          <PsychologyIcon />
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" noWrap>{persona.name}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap>{persona.key}</Typography>
          {persona.status && persona.status !== 'published' && (
            <Chip size="small" label={persona.status} sx={{ mt: 0.25, ml: 0, height: 18, fontSize: 10 }} />
          )}
          {mode !== 'picker' && trainingSharingLabel(persona) && (
            <Chip size="small" color="primary" variant="outlined" label={trainingSharingLabel(persona)} sx={{ mt: 0.25, ml: 0.5, height: 18, fontSize: 10 }} />
          )}
        </Box>
      </Box>

      <CardContent sx={{ pt: 0, flex: 1 }}>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          {conditions.slice(0, 4).map(c => (
            <Chip key={c} label={c} size="small" variant="outlined" />
          ))}
          {conditions.length > 4 && <Chip label={`+${conditions.length - 4}`} size="small" variant="outlined" />}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>
          {persona.description || persona.opening_line}
        </Typography>
        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {demo.ageRange && <Chip size="small" label={demo.ageRange} />}
          {demo.gender && <Chip size="small" label={demo.gender} />}
          {demo.culturalContextSummary && <Chip size="small" label={demo.culturalContextSummary} variant="outlined" />}
          {persona.metadata?.severity && <Chip size="small" label={persona.metadata.severity} color="warning" variant="outlined" />}
        </Box>
        {(isClone || similarity != null) && (
          <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
            {isClone && (
              <Tooltip title="Cloned from another persona">
                <Chip size="small" icon={<AccountTreeIcon />} label="clone" variant="outlined" />
              </Tooltip>
            )}
            {similarity != null && (
              <Tooltip title="Semantic similarity to your query">
                <Chip size="small" label={`sim ${(similarity * 100).toFixed(0)}%`} color="primary" variant="outlined" />
              </Tooltip>
            )}
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ justifyContent: 'space-between' }}>
        <Button size="small" startIcon={<VisibilityIcon />} onClick={onView}>View</Button>
        {isPicker && onSelect ? (
          <Button size="small" variant="contained" onClick={onSelect}>Select</Button>
        ) : onClone ? (
          <Button size="small" startIcon={<ContentCopyIcon />} onClick={onClone}>Clone</Button>
        ) : null}
      </CardActions>
    </Card>
  );
};

// ──────────────────────────────────────────────────────────────────────────────
// Detail drawer
// ──────────────────────────────────────────────────────────────────────────────

const PersonaDetailDrawer = ({ persona, token, mode = 'page', apiScope, onClose, onClone, onSelect, onSharingChanged }) => {
  const [tab, setTab] = useState(0);
  const [usage, setUsage] = useState(null);
  const [variants, setVariants] = useState([]);
  const [similar, setSimilar] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!persona) return;
    setTab(0);
    setUsage(null);
    setVariants([]);
    setSimilar([]);
    loadUsage();
    loadVariants();
    loadSimilar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persona?.key]);

  const loadUsage = async () => {
    if (!persona?.key) return;
    setLoading(true);
    try {
      const data = await getPersonaUsage(persona.key, token, apiScope);
      setUsage(data);
    } catch (e) {
      console.warn('usage load failed', e);
    } finally {
      setLoading(false);
    }
  };

  const loadVariants = async () => {
    if (!persona?.key) return;
    try {
      const data = await getPersonaVariants(persona.key, token, apiScope);
      setVariants(data.variants || []);
    } catch (e) {
      console.warn('variants load failed', e);
    }
  };

  const loadSimilar = async () => {
    if (!persona?.key) return;
    try {
      const data = await getSimilarPersonas(persona.key, 6, token, apiScope);
      setSimilar(data.similar || []);
    } catch (e) {
      console.warn('similar load failed', e);
    }
  };

  const handleRegenerate = async () => {
    if (!persona?.key) return;
    setLoading(true);
    try {
      await regeneratePersonaEmbedding(persona.key, token);
    } finally {
      setLoading(false);
    }
  };

  if (!persona) return null;
  const conditions = persona.metadata?.conditions || [];
  const demo = persona.metadata?.demographics || {};
  const cog = persona.cognitiveProfile || {};

  const usageCount = usage?.totalScenarios ?? 0;
  const isClone = !!persona.lineage?.clonedFrom;

  return (
    <Drawer anchor="right" open={!!persona} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', md: 560 } } }}>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <Avatar src={persona.personaImage?.url || undefined} sx={{ width: 64, height: 64 }}><PsychologyIcon /></Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap>{persona.name}</Typography>
            <Typography variant="caption" color="text.secondary">{persona.key}</Typography>
            {isClone && (
              <Box sx={{ mt: 0.5 }}>
                <Chip size="small" icon={<AccountTreeIcon />} label={`Customised from ${persona.lineage?.clonedFrom?.key || 'another persona'}`} variant="outlined" />
              </Box>
            )}
          </Box>
        </Stack>

        {usageCount > 0 && (
          <Alert severity="info" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
            This persona is used by <strong>{usageCount}</strong> scenario{usageCount === 1 ? '' : 's'}. Direct edits will affect all of them — use Clone if you only need to tweak this one.
          </Alert>
        )}

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          {mode === 'picker' && onSelect && (
            <Button variant="contained" color="primary" onClick={() => onSelect(persona)}>Use this persona</Button>
          )}
          {onClone && <Button variant={mode === 'picker' ? 'outlined' : 'contained'} startIcon={<ContentCopyIcon />} onClick={() => onClone(persona)}>Clone</Button>}
          {mode !== 'picker' && (
            <Button variant="outlined" startIcon={<EditIcon />} href={`/admin/dashboard/client-editor?key=${encodeURIComponent(persona.key)}`}>Open in editor</Button>
          )}
          <Button size="small" startIcon={<RefreshIcon />} onClick={handleRegenerate} disabled={loading}>Refresh embedding</Button>
        </Stack>

        {/* Admin library only: lecturers' picker (apiScope 'training') cannot change sharing. */}
        {mode !== 'picker' && apiScope !== 'training' && (
          <PersonaTrainingSharing
            persona={persona}
            token={token}
            onChanged={(trainingSharing) => { if (onSharingChanged) onSharingChanged(persona.key, trainingSharing); }}
          />
        )}

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
          <Tab label="Overview" />
          <Tab label={`Usage (${usageCount})`} />
          <Tab label={`Similar (${similar.length})`} />
          <Tab label={`Variants (${variants.length})`} />
          <Tab label="Prompt" />
        </Tabs>

        {tab === 0 && (
          <Box>
            <Section title="Description">{persona.description || '—'}</Section>
            <Section title="Conditions">
              {conditions.length ? conditions.map(c => <Chip key={c} label={c} size="small" sx={{ mr: 0.5, mb: 0.5 }} />) : '—'}
            </Section>
            <Section title="Demographics">
              <Row label="Age" value={demo.ageRange} />
              <Row label="Gender" value={demo.gender} />
              <Row label="Occupation" value={demo.occupation} />
              <Row label="Cultural context" value={demo.culturalContextSummary} />
              <Row label="Languages" value={(demo.languages || []).join(', ')} />
            </Section>
            <Section title="Cognitive profile">
              <Row label="Vocabulary" value={cog.vocabularyLevel} />
              <Row label="Emotional articulation" value={cog.emotionalArticulation} />
              <Row label="Introspective capacity" value={cog.introspectiveCapacity} />
              <Row label="Communication style" value={cog.communicationStyle} />
              <Row label="Education" value={cog.educationLevel} />
            </Section>
            {persona.metadata?.severity && (
              <Section title="Severity"><Chip label={persona.metadata.severity} color="warning" /></Section>
            )}
            {(persona.tags || []).length > 0 && (
              <Section title="Tags">
                {persona.tags.map(t => <Chip key={t} label={t} size="small" sx={{ mr: 0.5, mb: 0.5 }} />)}
              </Section>
            )}
          </Box>
        )}

        {tab === 1 && (
          <Box>
            {loading && <CircularProgress size={20} />}
            {usage?.scenarios?.length ? (
              <Stack spacing={1}>
                {usage.scenarios.map(s => (
                  <Paper key={s._id} variant="outlined" sx={{ p: 1 }}>
                    <Typography variant="subtitle2">{s.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {s.category} · {s.difficulty} · {s.status}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Typography color="text.secondary">No scenarios reference this persona yet.</Typography>
            )}
          </Box>
        )}

        {tab === 2 && (
          <Box>
            {similar.length === 0 ? (
              <Typography color="text.secondary">
                No similar personas found. Embeddings may still be generating — check back shortly, or run the backfill if this is a fresh install.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {similar.map(s => (
                  <Paper
                    key={s._id || s.key}
                    variant="outlined"
                    sx={{ p: 1, display: 'flex', gap: 1.5, alignItems: 'center', cursor: onSelect ? 'pointer' : 'default' }}
                    onClick={() => onSelect && onSelect(s)}
                  >
                    <Avatar src={s.personaImage?.url || undefined} sx={{ width: 40, height: 40 }}>
                      <PsychologyIcon fontSize="small" />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" noWrap>{s.name}</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Chip
                          label={`${(s._similarityScore * 100).toFixed(0)}% match`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 18, fontSize: 10 }}
                        />
                        {(s.metadata?.conditions || []).slice(0, 3).map(c => (
                          <Chip key={c} label={c} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                        ))}
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
        )}

        {tab === 3 && (
          <Box>
            {variants.length === 0 ? (
              <Typography color="text.secondary">No clones derived from this persona.</Typography>
            ) : (
              <Stack spacing={1}>
                {variants.map(v => (
                  <Paper key={v._id} variant="outlined" sx={{ p: 1 }}>
                    <Typography variant="subtitle2">{v.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {v.key} · {v.status} · created {new Date(v.createdAt).toLocaleDateString()}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
        )}

        {tab === 4 && (
          <Paper variant="outlined" sx={{ p: 1, maxHeight: 480, overflow: 'auto', fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: 12 }}>
            {persona.system_prompt || '—'}
          </Paper>
        )}
      </Box>
    </Drawer>
  );
};

const Section = ({ title, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="overline" color="text.secondary">{title}</Typography>
    <Box>{children}</Box>
  </Box>
);

const Row = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2">{value || '—'}</Typography>
  </Box>
);

// ──────────────────────────────────────────────────────────────────────────────
// Clone dialog
// ──────────────────────────────────────────────────────────────────────────────

const PersonaCloneDialog = ({ open, source, token, userRole, onClose, onCloned }) => {
  const [newKey, setNewKey] = useState('');
  const [newName, setNewName] = useState('');
  const [targetOrg, setTargetOrg] = useState(null);  // ServiceGroup object
  const [regenerateImage, setRegenerateImage] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [serviceGroups, setServiceGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  const platformAdmin = isPlatformAdminRole(userRole);

  useEffect(() => {
    if (!source) return;
    const shortId = Math.random().toString(36).slice(2, 6);
    setNewKey(`${source.key}-clone-${shortId}`);
    setNewName(`${source.name} (clone)`);
    setTargetOrg(null);
    setRegenerateImage(false);
    setError(null);
  }, [source]);

  // Lazy-load service groups only when a platform admin opens the dialog —
  // avoids a wasted fetch for therapists who can't use the field anyway.
  useEffect(() => {
    if (!open || !platformAdmin || serviceGroups.length > 0) return;
    setLoadingGroups(true);
    listServiceGroupsForPicker(token)
      .then(setServiceGroups)
      .catch(err => console.warn('Failed to load service groups:', err))
      .finally(() => setLoadingGroups(false));
  }, [open, platformAdmin, token, serviceGroups.length]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await clonePersona(source.key, {
        newKey,
        newName,
        targetOrgId: targetOrg?._id || undefined,
        regenerateImage
      }, token);
      onCloned();
    } catch (e) {
      setError(e.msg || e.message || 'Clone failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!source) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Clone "{source.name}"</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="New key"
            value={newKey}
            onChange={e => setNewKey(e.target.value)}
            helperText="lowercase alphanumeric with hyphens, minimum 3 characters"
            fullWidth
          />
          <TextField
            label="New name"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            fullWidth
          />
          {platformAdmin && (
            <Autocomplete
              options={serviceGroups}
              loading={loadingGroups}
              value={targetOrg}
              onChange={(_, v) => setTargetOrg(v)}
              getOptionLabel={(o) => o?.name || ''}
              isOptionEqualToValue={(o, v) => o?._id === v?._id}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{option.slug}</Typography>
                  </Box>
                  {option.isPlatformLibrary && (
                    <Chip size="small" label="Platform Library" color="primary" variant="outlined" />
                  )}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Target organisation"
                  helperText="Leave blank to keep the source persona's current org. Setting this copies the persona into another org (platform admins only)."
                  size="small"
                />
              )}
            />
          )}
          <FormControlLabel
            control={<Switch checked={regenerateImage} onChange={e => setRegenerateImage(e.target.checked)} />}
            label="Regenerate persona image (slower; default copies existing image URL)"
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting || !newKey || !newName}>
          {submitting ? 'Cloning…' : 'Clone'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientPersonaLibrary;
